import { Button, CircularProgress, Grid } from '@mui/material';
import {
  DataGrid,
  GridCellValue,
  GridColDef,
  GridRowParams,
  GridSelectionModel,
} from '@mui/x-data-grid';
import React, { useState } from 'react';
import { useFindAllInventoryHardwareQuery } from '../../api/api-hooks';
import { useGridSort } from '../../hooks/useGridSort';
import { useHardwareCategoryMap } from '../../hooks/useHardwareCategoryMap';
import { truncateISOString } from '../../utils/dateTime';
import { Item } from '../../utils/sharedTypes';
import DemoCreatorWithDefaults from '../demos/DemoCreatorWithDefaults';
import HardwareCreatorFromInventory from '../hardware/HardwareCreatorFromInventory';
import { filterInventoryHardwareItem } from './inventoryHardwareUtils';
import { Filters } from './inventoryTypes';

interface Props {
  setSelectedAnalyzers: React.Dispatch<React.SetStateAction<Item[]>>;
  filters: Filters;
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const InventoryAnalyzersTable = ({
  setSelectedAnalyzers,
  filters,
  setEditModalOpenFlag,
}: Props) => {
  const [clickedAnalyzer, setClickedAnalyzer] = useState<string | number>(0);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const { sortModel, handleSortModelChange } = useGridSort('id', 'desc');
  const categoryMap = useHardwareCategoryMap();
  const { data: analyzers } = useFindAllInventoryHardwareQuery({});
  if (analyzers === undefined) return <CircularProgress></CircularProgress>;

  const columns: GridColDef[] = [
    { field: 'id', hide: true },
    { field: 'serial', headerName: 'Serial Number', width: 200 },
    { field: 'category', headerName: 'Category', width: 200 },
    { field: 'type', headerName: 'Hardware Type', width: 200 },
    { field: 'macAddress', headerName: 'MAC Address', width: 200 },
    { field: 'dateReceived', headerName: 'Date Received', width: 200 },
    {
      field: 'subfields',
      headerName: ' ',
      width: 300,
      renderCell: (params) => {
        const openTransfer = (event: React.MouseEvent) => {
          event.stopPropagation();
          const analyzerId: GridCellValue = params.getValue(params.id, 'id');
          if (typeof analyzerId === 'number' || typeof analyzerId === 'string')
            setClickedAnalyzer(analyzerId);
          setTransferDialogOpen(true);
        };
        const openDemo = (event: React.MouseEvent) => {
          event.stopPropagation();
          const analyzerId: GridCellValue = params.getValue(params.id, 'id');
          if (typeof analyzerId === 'number' || typeof analyzerId === 'string')
            setClickedAnalyzer(analyzerId);
          setDemoDialogOpen(true);
        };
        return (
          <Grid container direction='row' spacing={1}>
            <Grid item>
              <Button variant='outlined' color='primary' onClick={openTransfer}>
                Mark Sold
              </Button>
            </Grid>
            <Grid item>
              <Button variant='outlined' color='primary' onClick={openDemo}>
                Make Demo
              </Button>
            </Grid>
          </Grid>
        );
      },
    },
  ];
  const rows = analyzers
    // Select analyzers for display if 1. the serial number matches the search term and 2. the type matches the current selection
    // and 3. the date received falls within the selected dates
    .filter((item) => filterInventoryHardwareItem(item, filters))
    .map((analyzer) => ({
      ...analyzer,
      type: analyzer.hardwareType.typeName,
      dateReceived:
        analyzer.dateReceived === null
          ? 'None'
          : truncateISOString(analyzer.dateReceived),
      category: categoryMap.get(analyzer.hardwareType.hardwareCategoryId),
    }));

  const handleTableSelection = (model: GridSelectionModel) => {
    setSelectedAnalyzers(
      analyzers.filter((analyzer) => model.includes(analyzer.id))
    );
  };

  const handleRowClick = (params: GridRowParams) => {
    setSelectedAnalyzers(
      analyzers.filter((analyzer) => analyzer.id === params.row.id)
    );
    setEditModalOpenFlag(true);
  };
  return (
    <>
      <HardwareCreatorFromInventory
        dialogOpen={transferDialogOpen}
        setDialogOpen={setTransferDialogOpen}
        analyzerId={clickedAnalyzer}
      />
      <DemoCreatorWithDefaults
        dialogOpen={demoDialogOpen}
        setDialogOpen={setDemoDialogOpen}
        analyzerId={Number(clickedAnalyzer)}
      />
      <div style={{ paddingBottom: '1rem', width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={100}
          autoHeight
          checkboxSelection
          disableSelectionOnClick
          disableColumnMenu={true}
          onSelectionModelChange={handleTableSelection}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onRowClick={handleRowClick}
        />
      </div>
    </>
  );
};

export default InventoryAnalyzersTable;
